import React, { useEffect, useState } from "react"
import { LuArrowRight, LuCheck, LuX } from "react-icons/lu"
import styles from "component/quotation/createVersionReasonPopup.module.scss"
import {
	canSubmitVersionReason,
	type IVersionReasonType,
	isVersionNoteRequired,
	toggleVersionReason,
} from "component/quotation/createVersionReasonUtil"

type Props = {
	isOpen: boolean
	fromVersionNo: number
	/** Shown as "Create V{next} from V{from}" when known. */
	nextVersionNo?: number
	reasonTypes: IVersionReasonType[]
	isLoadingReasons: boolean
	isSubmitting: boolean
	onClose: () => void
	onConfirm: (selectedReasonIds: number[], note: string) => void
}

const CreateVersionReasonPopup: React.FC<Props> = ({
	isOpen,
	fromVersionNo,
	nextVersionNo,
	reasonTypes,
	isLoadingReasons,
	isSubmitting,
	onClose,
	onConfirm,
}) => {

	const [selectedIds, setSelectedIds] = useState<number[]>([])
	const [note, setNote] = useState("")

	useEffect(() => {
		if (!isOpen) {
			setSelectedIds([])
			setNote("")
		}
	}, [isOpen])

	useEffect(() => {
		if (!isOpen) return
		const handleEscape = (event: KeyboardEvent) => {
			if (event.key === "Escape") onClose()
		}
		document.addEventListener("keydown", handleEscape)
		document.body.style.overflow = "hidden"
		return () => {
			document.removeEventListener("keydown", handleEscape)
			document.body.style.overflow = "unset"
		}
	}, [isOpen, onClose])

	if (!isOpen) return null

	const flagNoteRequired = isVersionNoteRequired(selectedIds, reasonTypes)
	const flagCanSubmit = !isSubmitting && canSubmitVersionReason(selectedIds, note, reasonTypes)
	const fromText = fromVersionNo > 0 ? `V${fromVersionNo}` : "this version"
	const createText = nextVersionNo ? `Create V${nextVersionNo} from ${fromText}.` : `Create a new version from ${fromText}.`

	return (
		<div
			className={styles.overlay}
			onClick={(event) => {
				if (event.target === event.currentTarget) onClose()
			}}
		>
			<div className={styles.dialog} role="dialog" aria-modal="true" aria-labelledby="create-version-title">
				<div className={styles.header}>
					<h2 id="create-version-title" className={styles.title}>Create new version</h2>
					<button type="button" className={styles.closeButton} onClick={onClose} disabled={isSubmitting} aria-label="Close">
						<LuX size={18} />
					</button>
				</div>
				<p className={styles.subtitle}>
					{createText} Tell your team why this quotation is changing before continuing to Edit.
				</p>

				<div className={styles.fieldLabel}>Reason for this version</div>
				{isLoadingReasons ? (
					<p className={styles.helperText}>Loading reasons...</p>
				) : reasonTypes.length === 0 ? (
					<p className={styles.helperText}>No reasons available.</p>
				) : (
					<div className={styles.reasonChips}>
						{reasonTypes.map((reason) => {
							const flagSelected = selectedIds.includes(reason.id)
							return (
								<button
									key={reason.id}
									type="button"
									className={[styles.reasonChip, flagSelected ? styles.reasonChipSelected : ""].filter(Boolean).join(" ")}
									onClick={() => setSelectedIds((previous) => toggleVersionReason(previous, reason.id))}
									aria-pressed={flagSelected}
									disabled={isSubmitting}
								>
									{flagSelected && <LuCheck size={14} />}
									{reason.name}
								</button>
							)
						})}
					</div>
				)}

				<label className={styles.fieldLabel} htmlFor="create-version-note">
					Note <span className={styles.fieldHint}>{flagNoteRequired ? "(required for Other)" : "(optional)"}</span>
				</label>
				<textarea
					id="create-version-note"
					className={styles.noteInput}
					value={note}
					onChange={(event) => setNote(event.target.value)}
					placeholder="Add details about what is changing..."
					disabled={isSubmitting}
				/>
				<p className={styles.helperText}>The reason and note appear in version previews and version history.</p>

				<div className={styles.actions}>
					<button type="button" className={styles.cancelButton} onClick={onClose} disabled={isSubmitting}>
						Cancel
					</button>
					<button
						type="button"
						className={styles.confirmButton}
						onClick={() => onConfirm(selectedIds, note)}
						disabled={!flagCanSubmit}
					>
						<LuArrowRight size={16} />
						{isSubmitting ? "Creating..." : "Continue to Edit"}
					</button>
				</div>
			</div>
		</div>
	)
}

export default CreateVersionReasonPopup
